/**
 * Как числа и даты выглядят на экране.
 *
 * В одном месте, потому что расхождение здесь читается как расхождение в
 * данных: одна и та же копия, показанная как «2,5 ТБ» и «2503 ГБ» на соседних
 * экранах, заставляет искать вторую копию.
 */

/** Объём. Единица выбирается по величине: тысячи гигабайт не читаются. */
export function bytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0';
  const tb = n / 1e12;
  if (tb >= 1) return `${tb.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} ТБ`;
  const gb = n / 1e9;
  if (gb >= 1) return `${gb.toLocaleString('ru-RU', { maximumFractionDigits: gb < 10 ? 1 : 0 })} ГБ`;
  const mb = n / 1e6;
  if (mb >= 1) return `${Math.round(mb).toLocaleString('ru-RU')} МБ`;
  return `${Math.round(n / 1e3).toLocaleString('ru-RU')} КБ`;
}

/** Давность. «—» значит «никогда», и это не то же, что «только что». */
export function ago(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'только что';
  const minutes = ms / 60000;
  if (minutes < 1) return 'сейчас';
  if (minutes < 60) return `${Math.round(minutes)} мин`;
  const hours = minutes / 60;
  if (hours < 48) return `${Math.round(hours)} ч`;
  return `${Math.round(hours / 24)} сут`;
}

/**
 * То же, но фразой целиком.
 *
 * `ago` отдаёт ДЛИТЕЛЬНОСТЬ — «5 мин», — и слово «назад» дописывало каждое
 * место вызова. Для длительности это верно, а для «сейчас» и «только что» —
 * нет: на экране выходило «видели сейчас назад». Решать это должен тот, кто
 * знает, число там или слово, то есть эта функция, а не десяток вызовов.
 */
export function agoPhrase(iso: string | null | undefined): string {
  const d = ago(iso);
  if (d === '—') return 'ни разу';
  // Слова живут без «назад», числа — с ним.
  return /\d/.test(d) ? `${d} назад` : d;
}

/** Сколько осталось. Отдельно от ago: выворачивать её наизнанку — писать шараду. */
export function until(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'истёк';
  const minutes = ms / 60000;
  if (minutes < 60) return `${Math.round(minutes)} мин`;
  const hours = minutes / 60;
  if (hours < 48) return `${Math.round(hours)} ч`;
  return `${Math.round(hours / 24)} сут`;
}

export function count(n: number): string {
  return n.toLocaleString('ru-RU');
}

/** Длительность прохода словами. */
export function duration(fromIso: string, toIso?: string | null): string {
  const ms = (toIso ? new Date(toIso).getTime() : Date.now()) - new Date(fromIso).getTime();
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s} с`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} мин ${s % 60} с`;
  return `${Math.floor(m / 60)} ч ${m % 60} мин`;
}

/**
 * Русское склонение при числе: 1 коммит, 2 коммита, 5 коммитов.
 *
 * «1 коммитов» на экране читается как недоделка и роняет доверие ко всему
 * остальному, что на нём написано.
 */
export function plural(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(n) % 100;
  if (abs >= 11 && abs <= 14) return many;
  const last = abs % 10;
  if (last === 1) return one;
  if (last >= 2 && last <= 4) return few;
  return many;
}
